import { useEffect, useState } from "react";
import { Box, Button, IconButton, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import "./inspection-firms.css";
import { useNavigate } from "react-router-dom";
import AddInspectionFirmDialog from "./add-inspection-firm/add-inspection-firm";
import Notification from "../../../components/notification/notification";
import Table from "../../../components/table/table";
import {
  GridColDef,
  GridRenderCellParams,
  GridRowSelectionModel,
} from "@mui/x-data-grid";
import Breadcrumb from "../../../components/breadcrumb/breadcrumb";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import FilterAltOffOutlinedIcon from "@mui/icons-material/FilterAltOffOutlined";
import TableHeaderTextField from "../../../components/table-header-text-filed/table-header-text-filed";
import { useDispatch, useSelector } from "react-redux";
import { getAllInspectionFirmsRequest } from "../../../redux/inspection-firms/slice";
import ContactSpectrum from "../../../components/contact-spectrum/constact-spectrum";

const NameRenderer = ({ rowData }: any) => {
  const navigate = useNavigate();
  const onViewDetailsClick = () => {
    navigate(`/inspection-firms/${rowData?.id}`, {
      state: {
        ...rowData,
        breadcrumb: {
          names: {
            [rowData?.id]: rowData?.displayName,
          },
        },
      },
    });
  };
  return (
    <Box>
      <Button onClick={onViewDetailsClick} sx={{ textTransform: "none" }}>
        <Typography>{rowData?.displayName}</Typography>
      </Button>
    </Box>
  );
};

// const StatusRenderer = ({ value }: any) => {
//     return (
//         <Box>
//             {value === "New" && (
//                 <Chip label={"New"} color="primary" style={{ width: 94, borderRadius: 8, height: 24 }} />
//             )}
//         </Box>
//     )
// };

export default function InspectionFirms() {
  const dispatch = useDispatch();
  const { loading, allInspectionFirms } = useSelector(
    (state: any) => state.inspectionFirms
  );

  const [openDialog, setOpenDialog] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [addedFirm, setAddedFirm] = useState<any>();
  const [rowData, setRowData] = useState<any[]>([]);
  const [showTableFilter, setShowTableFilter] = useState(true);
  const [headerHeight, setHeaderHeight] = useState(104);
  const [paginationModel, setPaginationModel] = useState<any>({
    page: 0,
    pageSize: 10,
  });
  const [filterParams, setFilterParams] = useState<any>({});
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);
  const [rowCount, setRowCount] = useState(0);

  // Columns with filter headers
  const column: GridColDef[] = [
    {
      field: "displayName",
      headerName: "Inspector Name",
      flex: 1,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => (
        <NameRenderer rowData={params.row} />
      ),
      renderHeader: (params) => (
        <TableHeaderTextField
          field="displayName"
          sortField="inspector_name"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterFirms}
        />
      ),
    },
    {
      field: "primaryRelationshipManager",
      headerName: "Relationship Manager (Primary)",
      flex: 1,
      sortable: false,
      renderHeader: (params) => (
        <TableHeaderTextField
          field="relationshipManager"
          sortField="relationship_manager"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterFirms}
        />
      ),
    },
  ];

  useEffect(() => {
    if (allInspectionFirms) {
      setRowData(allInspectionFirms.items || []);
      setRowCount(allInspectionFirms.totalCount || 0);
    }
  }, [allInspectionFirms]);

  useEffect(() => {
    const params = { ...filterParams };
    if (paginationModel) {
      params.page = paginationModel.page + 1;
      params.pageSize = paginationModel.pageSize;
    }
    dispatch(getAllInspectionFirmsRequest(params));
  }, [filterParams, paginationModel, dispatch]);

  const handleClick = () => {
    setOpenDialog(true);
  };

  const addFirm = (firm: any) => {
    setShowAlert(true);
    setAddedFirm(firm);
    setPaginationModel({
      page: 0,
      pageSize: 10,
    });
  };

  const onFilterClick = (value: boolean) => {
    setShowTableFilter(value);
    setHeaderHeight(value ? 104 : 56);
  };

  function onFilterFirms(data: any) {
    const keys = Object.keys(data);
    if (keys.includes("sortColumn")) {
      setFilterParams({ ...filterParams, ...data });
    } else {
      keys.forEach((key: string) => {
        if (filterParams.hasOwnProperty(key) && data[key] === "") {
          const { [key]: removedKey, ...filters } = filterParams as any;
          setFilterParams(filters);
        } else if (data[key] || data[key] === 0 || data[key] === false) {
          setFilterParams({ ...filterParams, [key]: data[key] });
        }
      });
    }
  }

  return (
    <>
      <ContactSpectrum />
      <Breadcrumb />
      <Typography sx={{ ml: 2 }} className="title">
        Inspection Firms
      </Typography>
      <Box
        sx={{ mt: 3 }}
        className="clients-header"
        justifyContent={"flex-end"}
      >
        <Button
          className="primary-bg"
          id="add-button"
          variant="contained"
          disableElevation
          onClick={handleClick}
          startIcon={<AddIcon />}
        >
          Add Inspection Firm
        </Button>
      </Box>

      <Box
        className="clients-header"
        sx={{ my: 1.5, justifyContent: "flex-end" }}
      >
        <IconButton
          aria-label="filter"
          onClick={() => onFilterClick(!showTableFilter)}
        >
          {showTableFilter ? (
            <FilterAltOffOutlinedIcon color="primary" />
          ) : (
            <FilterAltOutlinedIcon color="primary" />
          )}
        </IconButton>
      </Box>

      <Box sx={{ mx: 2 }}>
        <Table
          rowData={rowData}
          rowCount={rowCount}
          loading={loading}
          columnDef={column}
          headerHeight={headerHeight}
          disableColumnMenu={true}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          checkboxSelection={true}
          onRowSelectionModelChange={setRowSelectionModel}
          rowSelectionModel={rowSelectionModel}
        />
      </Box>

      <AddInspectionFirmDialog
        showDialog={openDialog}
        closeDialog={() => setOpenDialog(false)}
        addFirm={addFirm}
      />

      <Notification
        show={showAlert}
        title="Inspection Firm Added Successfully"
        message={`${addedFirm?.displayName} has been created.`}
        onClose={() => setShowAlert(false)}
      />
    </>
  );
}
